import { useRouter as useNextRouter } from 'next/router';
import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react';
import { useAppStore } from '@/src/store/global/index';
import { ROUTES } from '@/src/utils/routes';
import { TASK, TASK_QUERY_NAME } from '@/src/utils/constants';
import { NAVIGATION_TIMEOUT_MS } from '@/src/lib/SmartDB/Commons/Constants/constants';
import { NextRouter } from 'next/router';
import { pushWarningNotification } from '@/src/lib/SmartDB/Commons/pushNotification';
import NextLink, { LinkProps as NextLinkProps } from 'next/link';
import React, { forwardRef } from 'react';
import { ParsedUrlQueryInput } from 'querystring';

interface UrlObject {
    auth?: string | null | undefined;
    hash?: string | null | undefined;
    host?: string | null | undefined;
    hostname?: string | null | undefined;
    href?: string | null | undefined;
    pathname?: string | null | undefined;
    protocol?: string | null | undefined;
    search?: string | null | undefined;
    slashes?: boolean | null | undefined;
    port?: string | number | null | undefined;
    query?: string | null | ParsedUrlQueryInput | undefined;
}

interface TransitionOptions {
    shallow?: boolean;
    locale?: string | false;
    scroll?: boolean;
    unstable_skipClientCache?: boolean;
}

interface RouterContextType extends NextRouter {
    push: (url: UrlObject | string, as?: UrlObject | string | undefined, options?: TransitionOptions | undefined) => Promise<boolean>;
    replace: (url: UrlObject | string, as?: UrlObject | string | undefined, options?: TransitionOptions | undefined) => Promise<boolean>;
    isRouterReady: boolean;
}

const RouterContext = createContext<RouterContextType | null>(null);

interface RouterProviderProps {
    children: ReactNode;
}

export const RouterProvider: React.FC<RouterProviderProps> = ({ children }) => {
    const nextRouter = useNextRouter();
    const appStore = useAppStore();
    const [isReady, setIsReady] = useState(false);

    // Transaction navigation protection
    const confirmNavigation = useCallback(() => {
        if (appStore.isProcessingTx) {
            const result = window.confirm(
                'There is a transaction in progress. Are you sure you want to navigate away from this page? This might cause issues with your transaction.'
            );
            if (result === true) {
                appStore.setIsProcessingTx(false);
            }
            return result;
        }
        return true;
    }, [appStore.isProcessingTx]);

    // Route guard logic
    const guardNavigation = useCallback(
        async (url: string) => {
            // Skip checks during SSR
            if (typeof window === 'undefined') return true;
            // Skip if initial checks aren't complete
            if (appStore.swExistAnyWallet === undefined || appStore.swExistProtocol === undefined) {
                return true;
            }
            const isWalletRoute = url.includes(`${ROUTES.ADMIN}?${TASK_QUERY_NAME.TASK}=${TASK.WALLETS}`);
            const isProtocolRoute = url.includes(`${ROUTES.ADMIN}?${TASK_QUERY_NAME.TASK}=${TASK.PROTOCOLS}`);
            // Redirect if no wallet exists
            if (appStore.swExistAnyWallet === false) {
                if (!isWalletRoute) {
                    nextRouter.push({
                        pathname: ROUTES.ADMIN,
                        query: { [TASK_QUERY_NAME.TASK]: TASK.WALLETS },
                    });
                    return false;
                }
            }
            // Redirect if no protocol exists
            if (appStore.swExistAnyWallet === true && appStore.swExistProtocol === false) {
                if (!isProtocolRoute) {
                    nextRouter.push({
                        pathname: ROUTES.ADMIN,
                        query: { [TASK_QUERY_NAME.TASK]: TASK.PROTOCOLS, [TASK_QUERY_NAME.PROTOCOLS]: TASK.CREATE },
                    });
                    return false;
                }
            }

            return true;
        },
        [nextRouter, appStore.swExistAnyWallet, appStore.swExistProtocol]
    );

    // Enhanced push method
    const push = useCallback(
        async (...args: Parameters<typeof nextRouter.push>) => {
            // First check transaction in progress
            if (!confirmNavigation()) {
                return false;
            }
            // Then check route guards
            const destination =
                typeof args[0] === 'string'
                    ? args[0]
                    : typeof args[0] === 'object' && args[0] && 'pathname' in args[0] && args[0].pathname
                    ? (args[0].pathname as string)
                    : nextRouter.asPath || '';
            if (!(await guardNavigation(destination))) {
                return false;
            }
            try {
                const result = await nextRouter.push(...args);
                // Clear processing flag on successful navigation
                if (result !== false) {
                    // appStore.setIsProcessingTx(false);
                }
                return result;
            } catch (error) {
                // Also clear processing flag on error
                // appStore.setIsProcessingTx(false);
                throw error;
            }
        },
        [nextRouter, confirmNavigation, guardNavigation]
    );

    // Enhanced replace method
    const replace = useCallback(
        async (...args: Parameters<typeof nextRouter.replace>) => {
            // First check transaction in progress
            if (!confirmNavigation()) {
                return false;
            }
            // Then check route guards
            const destination =
                typeof args[0] === 'string'
                    ? args[0]
                    : typeof args[0] === 'object' && args[0] && 'pathname' in args[0] && args[0].pathname
                    ? (args[0].pathname as string)
                    : nextRouter.asPath || '';
            if (!(await guardNavigation(destination))) {
                return false;
            }
            try {
                const result = await nextRouter.replace(...args);
                // Clear processing flag on successful navigation
                // if (result !== false) {
                //     appStore.setIsProcessingTx(false);
                // }
                return result;
            } catch (error) {
                // Also clear processing flag on error
                // appStore.setIsProcessingTx(false);
                throw error;
            }
        },
        [nextRouter, confirmNavigation, guardNavigation]
    );

    // Check current route on mount and run route guard
    useEffect(() => {
        if (!nextRouter.isReady) return;
        const checkRouteAndProtocol = async () => {
            await guardNavigation(nextRouter.asPath);
            setIsReady(true);
        };
        checkRouteAndProtocol();
    }, [nextRouter.isReady, guardNavigation]);

    useEffect(() => {
        const handleRouteChangeStart = (url: string) => {
            console.log(`[Navigation] Starting to change route to: ${url}`);
            appStore.setIsNavigating(true);
            // Set a timeout to clear navigation state if it gets stuck
            const timeoutId = setTimeout(() => {
                console.log('[Navigation] Navigation timeout - resetting state');
                appStore.setIsNavigating(false);
                // appStore.setIsProcessingTx(false);
            }, NAVIGATION_TIMEOUT_MS); // 10 seconds timeout

            // Store timeout ID to cancel it on successful navigation
            appStore.setNavigationTimeoutId(timeoutId);
        };

        const handleRouteChangeComplete = (url: string) => {
            console.log(`[Navigation] Finished changing route to: ${url}`);
            // Clear timeout and reset states
            if (appStore.navigationTimeoutId !== null) {
                clearTimeout(appStore.navigationTimeoutId);
            }
            appStore.setIsNavigating(false);
            // appStore.setIsProcessingTx(false);
        };

        const handleRouteChangeError = (err: any, url: string) => {
            // Clear timeout regardless of error type
            if (appStore.navigationTimeoutId !== null) {
                clearTimeout(appStore.navigationTimeoutId);
            }

            // Ignore route cancellations (checking for both message formats)
            if (
                err?.message === 'Route Cancelled' ||
                err?.message?.includes('Cancel rendering route') ||
                err?.message?.includes('Loading initial props cancelled') ||
                err?.cancelled
            ) {
                console.log(`[Navigation] Route change to ${url} was cancelled`);
                appStore.setIsNavigating(false);
                // appStore.setIsProcessingTx(false);
                return;
            }
            console.error(`[Navigation] Route change to ${url} failed with error`, err);
            appStore.setIsNavigating(false);
            // appStore.setIsProcessingTx(false);
        };

        nextRouter.events.on('routeChangeStart', handleRouteChangeStart);
        nextRouter.events.on('routeChangeComplete', handleRouteChangeComplete);
        nextRouter.events.on('routeChangeError', handleRouteChangeError);

        // Cleanup event listeners on component unmount
        return () => {
            nextRouter.events.off('routeChangeStart', handleRouteChangeStart);
            nextRouter.events.off('routeChangeComplete', handleRouteChangeComplete);
            nextRouter.events.off('routeChangeError', handleRouteChangeError);
        };
    }, [nextRouter.events]);

    return <RouterContext.Provider value={{ ...nextRouter, push, replace, isRouterReady: isReady && nextRouter.isReady }}>{children}</RouterContext.Provider>;
};

export const useRouter = () => {
    const context = useContext(RouterContext);

    if (!context) {
        throw new Error('useRouter must be used within a context of RouterProvider');
    }
    return context;
};
